import { useCallback, useMemo, useState } from "react";
import { Pressable, StyleSheet, useWindowDimensions, View } from "react-native";
import { NavigationProp, useNavigation } from "@react-navigation/native";

import { useGetAllCompanionChatsForUserQuery } from "../../../graphql/generated/graphql";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import { colors, radii, semanticColors, spacing, typographyScale } from "../../../styles/design-system";
import { useWorkspaceThreads } from "../../../contexts/CompanionThreadsContext";
import { GENERAL_WORKSPACE_ID } from "../../../hooks/useWorkspaceLists";
import { ArrowRight, Clock, MessageSquare } from "lucide-react";
import CustomText from "../../common/general/CustomText/CustomText";
import { formatTimeAgo } from "../../../utils/date";

export const RecentChat = () => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { width } = useWindowDimensions();
  const isMedium = width >= 768;
  const { setActiveWorkspaceId, setActiveThreadId } = useWorkspaceThreads();
  const { data: chatsData } = useGetAllCompanionChatsForUserQuery({});
  const generalChats = chatsData?.getAllCompanionChatsForUser?.generalChats ?? [];
  const courseWorkspaces = chatsData?.getAllCompanionChatsForUser?.courseWorkspaces ?? [];

  const handleChatClick = useCallback(
    (workspaceId: string, chatId: string) => {
      setActiveWorkspaceId(workspaceId);
      setActiveThreadId(chatId);
      navigation.navigate("Companion Chat", { chatId });
    },
    [navigation, setActiveWorkspaceId, setActiveThreadId]
  );

  const [hover, setHover] = useState<string | undefined>();
  const [hoveredBadgeId, setHoveredBadgeId] = useState<string | undefined>();

  const displayChats = useMemo(() => {
    const allChats: Array<{
      id: string;
      name?: string | null;
      last_modified?: string | null;
      workspaceId: string;
      workspaceName: string;
      workspaceType: "general" | "course";
    }> = generalChats.map((chat) => ({
      id: chat.id,
      name: chat.name,
      last_modified: chat.last_modified,
      workspaceId: GENERAL_WORKSPACE_ID,
      workspaceName: "General Chat",
      workspaceType: "general",
    }));

    courseWorkspaces.forEach((workspace) => {
      workspace.chats?.forEach((chat) => {
        allChats.push({
          id: chat.id,
          name: chat.name,
          last_modified: chat.last_modified,
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          workspaceType: "course",
        });
      });
    });

    return allChats
      .sort((a, b) => new Date(b.last_modified ?? "").getTime() - new Date(a.last_modified ?? "").getTime())
      .slice(0, 3);
  }, [generalChats, courseWorkspaces]);

  const headerIconSize = isMedium ? 20 : 18;
  const itemIconSize = 16;

  const getBadgePalette = (workspaceType: "general" | "course") => {
    switch (workspaceType) {
      case "course":
        return {
          backgroundColor: colors.brand[100],
          hoverBackgroundColor: `${colors.brand[200]}cc`,
          textColor: colors.brand[900],
        };
      default:
        return {
          backgroundColor: colors.grey[100],
          hoverBackgroundColor: `${colors.grey[200]}cc`,
          textColor: colors.grey[900],
        };
    }
  };

  return (
    <View style={[styles.container, isMedium && styles.containerMd]}>
      <View style={[styles.header, isMedium && styles.headerMd]}>
        <Clock size={headerIconSize} color={colors.grey[600]} />
        <CustomText style={styles.headerText} text="Recent Chats" />
      </View>
      {displayChats.length > 0 ? (
        <View style={[styles.list, isMedium && styles.listMd]}>
          {displayChats.map((chat) => {
            const timeText = chat.last_modified ? formatTimeAgo(chat.last_modified) : "";

            const chatTitle = chat.name || "Main Thread";
            const badgePalette = getBadgePalette(chat.workspaceType);

            return (
              <Pressable
                key={chat.id}
                style={[styles.listItem, isMedium && styles.listItemMd, hover === chat.id && styles.activeItem]}
                onPress={() => handleChatClick(chat.workspaceId, chat.id)}
                onMouseEnter={() => setHover(chat.id)}
                onMouseLeave={() => setHover(undefined)}>
                {isMedium && <MessageSquare size={itemIconSize} color={colors.grey[500]} />}
                <View style={styles.itemContent}>
                  <CustomText
                    style={[styles.itemName, hover === chat.id && styles.activeItemName]}
                    text={chatTitle}
                    useTranslationText={false}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  />
                  <View style={[styles.itemMetaRow, isMedium && styles.itemMetaRowMd]}>
                    <Pressable
                      onPress={(event) => {
                        event.stopPropagation?.();
                        handleChatClick(chat.workspaceId, chat.id);
                      }}
                      onMouseEnter={() => setHoveredBadgeId(chat.id)}
                      onMouseLeave={() => setHoveredBadgeId(undefined)}
                      accessibilityRole="button"
                      accessibilityLabel={`Open ${chat.workspaceName} workspace`}
                      style={[
                        styles.badge,
                        { backgroundColor: badgePalette.backgroundColor },
                        hoveredBadgeId === chat.id && { backgroundColor: badgePalette.hoverBackgroundColor },
                      ]}>
                      <CustomText
                        style={[styles.badgeText, { color: badgePalette.textColor }]}
                        text={chat.workspaceName}
                        useTranslationText={false}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      />
                    </Pressable>
                    <CustomText
                      style={styles.itemTime}
                      text={timeText}
                      useTranslationText={false}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    />
                  </View>
                </View>
                {isMedium && (
                  <ArrowRight size={16} color={colors.grey[400]} style={{ opacity: hover === chat.id ? 1 : 0 }} />
                )}
              </Pressable>
            );
          })}
        </View>
      ) : (
        <View style={styles.emptyState}>
          <Clock size={28} color={colors.grey[300]} />
          <CustomText style={styles.emptyTitle} text="No upcoming assignments" useTranslationText={false} />
          <CustomText
            style={styles.emptySubtitle}
            text="Assignments from your classes will appear here"
            useTranslationText={false}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignSelf: "stretch",
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: "rgba(208, 213, 221, 0.5)",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: spacing.sm,
    shadowColor: colors.grey[900],
    shadowOffset: {
      width: 0,
      height: 24,
    },
    shadowOpacity: 0.18,
    shadowRadius: 48,
    overflow: "hidden",
    // @ts-expect-error - web-only blur
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },
  containerMd: {
    padding: spacing.md,
    maxWidth: 320,
    minWidth: 260,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    marginBottom: 12,
  },
  headerMd: {
    marginBottom: 12,
  },
  headerText: {
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: 600,
  },
  list: {
    padding: spacing.xs,
    display: "flex",
    gap: spacing.xs,
    height: 300,
  },
  listMd: {
    height: "auto",
  },
  listItem: {
    display: "flex",
    flexDirection: "row",
    gap: spacing.xs,
    borderRadius: radii.md,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    marginHorizontal: -spacing.xs,
    cursor: "pointer",
  },
  listItemMd: {
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    marginHorizontal: -12,
  },
  activeItem: {
    backgroundColor: colors.grey[50],
  },
  itemContent: {
    flex: 1,
  },
  itemName: {
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: 500,
    lineHeight: 21,
  },
  itemMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    marginTop: spacing.xxs,
  },
  itemMetaRowMd: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: spacing.xxs,
  },
  badge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: radii.full,
    cursor: "pointer",
  },
  badgeText: {
    fontSize: 11,
    fontWeight: 600,
  },
  activeItemName: {
    color: semanticColors.typography.textBrand,
  },
  itemTime: {
    color: semanticColors.typography.textLight,
    fontSize: typographyScale.textXs.fontSize,
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: spacing.lg,
    gap: 6,
  },
  emptyTitle: {
    color: colors.grey[500],
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: 500,
  },
  emptySubtitle: {
    color: colors.grey[400],
    fontSize: typographyScale.textXs.fontSize,
  },
});
